import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Plus, Trash2, Check, RefreshCw, Printer, Package, FileText, AlertTriangle, CheckCircle, Scan, ChevronRight, Edit2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import * as fobApi from '@/services/fobSectionApi';
import * as authApi from '@/services/authApi';
import PrintDialog from '@/components/PrintDialog';
import FGStickerPrintDialog from '@/components/FGStickerPrintDialog';
import { validateBundleFormat } from '@/utils/validation';
import { SortConsolidatedTable } from './SortConsolidatedTable';
import { LineProgress, isPcs, overMsg } from '@/components/po/PoLinePicker';
import { SortPoRows, overflowAsk } from '@/components/po/SortPoRows';
import { NextPoDialog } from '@/components/po/NextPoDialog';
import { SearchSelect } from '@/components/po/SearchSelect';

const SortNoUsnTagging = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [consignments, setConsignments] = useState([]);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [sortEntry, setSortEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Current step in workflow - MODIFIED for per-bundle workflow
  // Steps: 'document' -> 'bundles' (scan, add details, assign USN, print per bundle)
  const [currentStep, setCurrentStep] = useState('document');

  // Progress tracking state (Assigned Qty / Document Qty)
  const [assignmentProgress, setAssignmentProgress] = useState({
    assigned_qty: 0,
    document_qty: 0,
    progress_percentage: 0
  });

  // Bundle scanning state
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [currentBundle, setCurrentBundle] = useState(null);
  const bundleInputRef = useRef(null);

  // Sort No selection with search
  const [sortNos, setSortNos] = useState([]);
  const [selectedSortNo, setSelectedSortNo] = useState('');
  const [sortNoPopoverOpen, setSortNoPopoverOpen] = useState(false);
  const [sortNoSearch, setSortNoSearch] = useState('');

  // Sort entry form for current bundle - now using dropdown
  const [sortNo, setSortNo] = useState('');
  const [actualQty, setActualQty] = useState('');
  const [fgBarcode, setFgBarcode] = useState(null); // FG Barcode info for current sort number
  // Purchase Order row the tagged qty is counted on: pick the sort no → its pending PO rows (kept across bundles)
  const [poSel, setPoSel] = useState({ po: null, line: null, pct: 5, none: false, rows: [] });
  const [poSortNo, setPoSortNo] = useState('');
  const [ask, setAsk] = useState(null);
  const [poKey, setPoKey] = useState(0);
  const [poQty, setPoQty] = useState('');

  // Summary editing state
  const [editingSortNo, setEditingSortNo] = useState(null);
  const [billQtyInput, setBillQtyInput] = useState('');
  const [remarksInput, setRemarksInput] = useState('');

  // USN Dialog for bundle printing
  const [showUsnDialog, setShowUsnDialog] = useState(false);
  const [selectedBundleUsns, setSelectedBundleUsns] = useState({ bundle: '', usns: [] });

  // NEW: USN Dialog for viewing USNs by Sort No from summary table
  const [showSortUsnDialog, setShowSortUsnDialog] = useState(false);
  const [selectedSortUsns, setSelectedSortUsns] = useState({ sort_no: '', bundles: [], total_usns: 0 });

  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCodes, setPrintCodes] = useState([]);
  const [printSortNos, setPrintSortNos] = useState([]);
  const [printingBundle, setPrintingBundle] = useState(''); // bundle whose stickers are being printed (for the print-status stamp)

  // FG Sticker (main label) print dialog state
  const [showFgStickerDialog, setShowFgStickerDialog] = useState(false);
  const [fgStickerItems, setFgStickerItems] = useState([]);

  // Edit barcode dialog state
  const [showEditBarcodeDialog, setShowEditBarcodeDialog] = useState(false);
  const [newBarcode, setNewBarcode] = useState('');

  useEffect(() => {
    fetchUser();
    fetchConsignments();
  }, []);

  // Fetch sort nos when consignment is selected (vendor comes from document)
  useEffect(() => {
    if (selectedConsignment?.vendor_name) {
      fetchSortNos(selectedConsignment.vendor_name);
    } else {
      setSortNos([]);
      setSelectedSortNo('');
    }
  }, [selectedConsignment]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchConsignments = async () => {
    setIsLoading(true);
    try {
      const data = await fobApi.getUsnReadyConsignments();
      setConsignments(data.consignments || []);
    } catch (error) {
      console.error('Error fetching consignments:', error);
      toast.error('Failed to load consignments');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSortNos = async (vendorName) => {
    try {
      const data = await fobApi.getSortNosByVendor(vendorName);
      setSortNos(data.sort_nos || []);
    } catch (error) {
      console.error('Error fetching sort nos:', error);
      toast.error('Failed to load sort numbers');
    }
  };

  // Filter sort nos based on search
  const filteredSortNos = useMemo(() => {
    if (!sortNoSearch) return sortNos;
    const search = sortNoSearch.toLowerCase();
    return sortNos.filter(s => 
      s.sort_no.toLowerCase().includes(search) ||
      (s.design_name && s.design_name.toLowerCase().includes(search)) ||
      (s.description && s.description.toLowerCase().includes(search))
    );
  }, [sortNos, sortNoSearch]);

  const handleSortNoDropdownSelect = (sortNoData) => {
    setSelectedSortNo(sortNoData.sort_no);
    setSortNo(sortNoData.sort_no);
    setSortNoPopoverOpen(false);
    setSortNoSearch('');
    
    // Set FG barcode info from the sort data
    if (sortNoData.fg_name || sortNoData.design_name) {
      setFgBarcode({
        barcode: sortNoData.fg_name,
        size: sortNoData.size,
        vendor: sortNoData.vendor_name,
        designName: sortNoData.design_name
      });
    } else {
      setFgBarcode(null);
    }
  };

  const getSelectedSortNoData = () => {
    return sortNos.find(s => s.sort_no === selectedSortNo);
  };

  const poLine = poSel.po && poSel.line ? poSel.line : null;
  const lineSortLocked = !!poLine;
  const applyLineSort = (sn = poSortNo) => {
    if (!sn) return;
    const data = sortNos.find(s => s.sort_no === sn);
    if (data) handleSortNoDropdownSelect(data);
    else { setSelectedSortNo(sn); setSortNo(sn); setFgBarcode(null); }
  };
  const handlePoChange = (sel) => {
    if (sel.switched && poSel.line) toast.info(`${poSel.po?.po_number || 'The PO'} is no longer open${sel.line ? ` — now counting on ${sel.po.po_number}` : ' — pick the next PO row'}`, { duration: 8000 });
    setPoSel(sel);
    if (sel.line) applyLineSort();
  };
  const pickPoSort = (sn) => {
    setPoSortNo(sn);
    setPoSel((s) => ({ ...s, po: null, line: null, none: false }));
    applyLineSort(sn);
  };

  // Fetch assignment progress
  const fetchAssignmentProgress = async (entryId) => {
    try {
      const progress = await fobApi.getBundleAssignmentProgress(entryId);
      setAssignmentProgress({
        assigned_qty: progress.assigned_qty || 0,
        document_qty: progress.document_qty || 0,
        progress_percentage: progress.progress_percentage || 0
      });
    } catch (error) {
      console.error('Error fetching assignment progress:', error);
    }
  };

  const handleSelectConsignment = async (entryId) => {
    const consignment = consignments.find(c => c.entry_id === entryId);
    setSelectedConsignment(consignment);
    resetAllState();
    
    try {
      const entry = await fobApi.startSortEntry(entryId);
      setSortEntry(entry);
      
      // Fetch assignment progress
      await fetchAssignmentProgress(entryId);
      
      // For per-bundle workflow, always stay in 'bundles' step
      // unless all bundles are completed
      setCurrentStep('bundles');
    } catch (error) {
      console.error('Error starting sort entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to start sort entry');
    }
  };

  const resetAllState = () => {
    setBundleBarcode('');
    setCurrentBundle(null);
    setSortNo('');
    setSelectedSortNo('');
    setActualQty('');
    setFgBarcode(null);
    setEditingSortNo(null);
    setBillQtyInput('');
    setRemarksInput('');
    setCurrentStep('document');
    setAssignmentProgress({ assigned_qty: 0, document_qty: 0, progress_percentage: 0 });
    setPoSel({ po: null, line: null, pct: 5, none: false, rows: [] });
    setPoSortNo('');
    setPoQty('');
  };

  const resetBundleForm = () => {
    setSortNo('');
    setSelectedSortNo('');
    setActualQty('');
    setFgBarcode(null);
    setPoQty('');
    applyLineSort();
  };

  // ========================
  // STEP 2: Bundle Scanning
  // ========================
  const handleScanBundle = async (e) => {
    e?.preventDefault();
    
    if (!bundleBarcode.trim()) {
      toast.error('Please scan or enter bundle barcode');
      return;
    }

    // Validate bundle format - must start with BUN and be 15 characters
    const formatValidation = validateBundleFormat(bundleBarcode.trim());
    if (!formatValidation.valid) {
      toast.error(formatValidation.error);
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await fobApi.validateBundleForSort(selectedConsignment.entry_id, bundleBarcode.trim());
      
      setCurrentBundle({
        barcode: bundleBarcode.trim(),
        alreadyProcessed: result.already_processed,
        existingEntries: result.existing_entries || []
      });
      setActualQty('');
      setPoQty('');
      
      if (result.already_processed) {
        toast.info('This bundle already has entries. You can add more.');
      }
      
      setBundleBarcode('');
    } catch (error) {
      console.error('Error validating bundle:', error);
      toast.error(error.response?.data?.detail || 'Invalid bundle barcode');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddBundleSort = async (e) => {
    e.preventDefault();
    
    if (!currentBundle) {
      toast.error('Please scan a bundle first');
      return;
    }
    
    if (!sortNo.trim() || !actualQty) {
      toast.error('Sort No and Actual Qty are required');
      return;
    }
    if ((poSel.rows || []).length && !poLine && !poSel.none) {
      toast.error(`Pick the Purchase Order row of sort no ${poSortNo || sortNo} this bundle is counted on`);
      return;
    }
    const linePcs = isPcs(poLine?.unit);
    if (poLine && !linePcs && !(parseFloat(poQty) > 0)) {
      toast.error(`PO line ${poLine.supplier_design_name} is in ${poLine.unit} — enter the ${poLine.unit} for this bundle`);
      return;
    }
    if (poLine) {
      const a = overflowAsk({ row: { po: poSel.po, line: poLine }, rows: poSel.rows, qty: linePcs ? parseInt(actualQty) : parseFloat(poQty),
        usedOn: () => 0, what: `Bundle ${currentBundle.barcode}` });
      if (a) { setAsk(a); return; }
    }
    await addSort(poSel.po, poLine);
  };

  // Next PO picked for a bundle past the pending qty — the whole bundle goes there (asked again if it doesn't fit either)
  const onNextPo = (r) => {
    setPoSel((s) => ({ ...s, po: r.po, line: r.line, none: false }));
    const pcs = isPcs(r.line.unit);
    const a = overflowAsk({ row: r, rows: poSel.rows, qty: pcs ? parseInt(actualQty) : parseFloat(poQty), usedOn: () => 0, what: `Bundle ${currentBundle.barcode}` });
    setAsk(a);
    if (!a) addSort(r.po, r.line);
  };

  const addSort = async (po, line) => {
    const linePcs = isPcs(line?.unit);
    const over = line && overMsg(po, line, poSel.pct, linePcs ? parseInt(actualQty) : parseFloat(poQty));
    if (over) { toast.error(over, { duration: 9000 }); return; }

    setIsSubmitting(true);
    try {
      const result = await fobApi.addBundleSort(
        selectedConsignment.entry_id,
        currentBundle.barcode,
        sortNo.trim(),
        parseInt(actualQty),
        line?.po_item_id || null,
        line && !linePcs ? parseFloat(poQty) : null
      );
      
      toast.success(line ? `${result.message} · counted on ${po.po_number}` : result.message);
      setPoKey((k) => k + 1);
      
      // Refresh sort entry
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      
      // Update current bundle's existing entries
      const updatedBundle = entry.bundles?.find(b => b.bundle_barcode === currentBundle.barcode);
      if (updatedBundle) {
        setCurrentBundle({
          ...currentBundle,
          alreadyProcessed: true,
          existingEntries: updatedBundle.sort_entries || []
        });
      }
      
      resetBundleForm();
    } catch (error) {
      console.error('Error adding sort entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to add sort entry', { duration: 9000 });
      setPoKey((k) => k + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveBundleSort = async (bundleBarcode, sortNoToRemove) => {
    if (!window.confirm(`Remove Sort No ${sortNoToRemove} from bundle?`)) return;
    
    try {
      await fobApi.removeBundleSort(selectedConsignment.entry_id, bundleBarcode, sortNoToRemove);
      toast.success('Sort entry removed');
      setPoKey((k) => k + 1);
      
      // Refresh
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      
      // Update current bundle if it's the one we're editing
      if (currentBundle?.barcode === bundleBarcode) {
        const updatedBundle = entry.bundles?.find(b => b.bundle_barcode === bundleBarcode);
        if (updatedBundle) {
          setCurrentBundle({
            ...currentBundle,
            existingEntries: updatedBundle.sort_entries || []
          });
        } else {
          setCurrentBundle(null);
        }
      }
    } catch (error) {
      console.error('Error removing sort entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove sort entry');
    }
  };

  const handleNextBundle = () => {
    setCurrentBundle(null);
    setBundleBarcode('');
    setTimeout(() => bundleInputRef.current?.focus(), 100);
  };

  // ========================
  // NEW: Per-Bundle USN Assignment & Print
  // ========================
  const handleAssignBundleUsn = async () => {
    if (!currentBundle) {
      toast.error('No bundle selected');
      return;
    }
    
    if (!currentBundle.existingEntries || currentBundle.existingEntries.length === 0) {
      toast.error('Please add at least one sort entry before assigning USN');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await fobApi.assignBundleUsn(selectedConsignment.entry_id, currentBundle.barcode);
      toast.success(result.message);
      
      // Refresh sort entry and progress
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      await fetchAssignmentProgress(selectedConsignment.entry_id);
      
      // Update current bundle state
      const updatedBundle = entry.bundles?.find(b => b.bundle_barcode === currentBundle.barcode);
      if (updatedBundle) {
        setCurrentBundle({
          ...currentBundle,
          alreadyProcessed: true,
          existingEntries: updatedBundle.sort_entries || [],
          usnAssigned: updatedBundle.usn_assigned || false
        });
      }
    } catch (error) {
      console.error('Error assigning USN to bundle:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign USN');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintCurrentBundle = async () => {
    if (!currentBundle) return;
    setPrintingBundle(currentBundle.barcode);
    
    try {
      const result = await fobApi.getBundleUsns(selectedConsignment.entry_id, currentBundle.barcode);
      const allUsns = [];
      const allSortNos = [];
      for (const sortEntry of result.sort_entries || []) {
        const sortNo = sortEntry.sort_no;
        const usns = sortEntry.usns || [];
        allUsns.push(...usns);
        allSortNos.push(...usns.map(() => `Sort: ${sortNo}`));
      }
      
      if (allUsns.length === 0) {
        toast.error('No USNs found for this bundle');
        return;
      }
      
      setPrintCodes(allUsns);
      setPrintSortNos(allSortNos);
      setShowPrintDialog(true);
    } catch (error) {
      console.error('Error fetching bundle USNs for print:', error);
      toast.error(error.response?.data?.detail || 'Failed to load USNs for printing');
    }
  };

  const handlePrintCurrentBundleFGSticker = async () => {
    if (!currentBundle) return;
    setPrintingBundle(currentBundle.barcode);
    try {
      const result = await fobApi.getBundleUsns(selectedConsignment.entry_id, currentBundle.barcode);
      const items = [];
      for (const se of result.sort_entries || []) {
        for (const usn of se.usns || []) {
          items.push({ usn_code: usn, fg_barcode: se.fg_barcode || '' });
        }
      }
      if (items.length === 0) {
        toast.error('No USNs found for this bundle');
        return;
      }
      setFgStickerItems(items);
      setShowFgStickerDialog(true);
    } catch (error) {
      console.error('Error loading FG stickers:', error);
      toast.error(error.response?.data?.detail || 'Failed to load FG stickers');
    }
  };

  // Stamp the bundle with a print timestamp after EVERY successful print (USN
  // stickers or FG sticker) so the Bundles Processed table shows a live print
  // status + last-printed time. This does NOT complete/advance the bundle.
  const handleRecordPrint = async () => {
    if (!printingBundle || !selectedConsignment) return;
    try {
      await fobApi.recordBundlePrint(selectedConsignment.entry_id, printingBundle);
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
    } catch (error) {
      console.error('Error recording bundle print:', error);
    }
  };

  const handleMarkCurrentBundlePrinted = async () => {    if (!currentBundle) return;
    
    try {
      const result = await fobApi.markBundlePrinted(selectedConsignment.entry_id, currentBundle.barcode);
      toast.success('Bundle marked as completed!');
      
      // Refresh sort entry and progress
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      await fetchAssignmentProgress(selectedConsignment.entry_id);
      
      // Move to next bundle
      setCurrentBundle(null);
      setBundleBarcode('');
      setTimeout(() => bundleInputRef.current?.focus(), 100);
      
      if (result.all_printed) {
        toast.success('All bundles completed! USN Tagging finished.');
      }
    } catch (error) {
      console.error('Error marking bundle as printed:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as printed');
    }
  };

  // Legacy function - kept for backward compatibility
  const handleCompleteBundles = async () => {
    setIsSubmitting(true);
    try {
      const result = await fobApi.completeBundles(selectedConsignment.entry_id);
      toast.success(result.message);
      
      // Refresh and move to summary step
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      setCurrentStep('summary');
      setCurrentBundle(null);
    } catch (error) {
      console.error('Error completing bundles:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete bundles');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================
  // STEP 3: Summary & Bill Qty (Legacy - kept for backward compatibility)
  // ========================
  const handleEditBillQty = (sortSummary) => {
    setEditingSortNo(sortSummary.sort_no);
    setBillQtyInput(sortSummary.bill_qty || sortSummary.aggregated_qty);
    setRemarksInput(sortSummary.remarks || '');
  };

  const handleSaveBillQty = async () => {
    if (!billQtyInput) {
      toast.error('Bill Qty is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const aggregated = sortEntry.sort_summary.find(s => s.sort_no === editingSortNo)?.aggregated_qty || 0;
      const shortage = Math.max(0, parseInt(billQtyInput) - aggregated);
      const excess = Math.max(0, aggregated - parseInt(billQtyInput));
      
      // Check if remarks required
      if ((shortage > 0 || excess > 0) && !remarksInput.trim()) {
        toast.error('Remarks is required when there is shortage or excess');
        return;
      }

      await fobApi.updateSummaryBillQty(
        selectedConsignment.entry_id,
        editingSortNo,
        parseInt(billQtyInput),
        remarksInput.trim() || null
      );
      
      toast.success('Bill Qty updated');
      
      // Refresh
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      setEditingSortNo(null);
      setBillQtyInput('');
      setRemarksInput('');
    } catch (error) {
      console.error('Error updating bill qty:', error);
      toast.error(error.response?.data?.detail || 'Failed to update bill qty');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmSummary = async () => {
    // Check all bill_qty are set
    const unsetEntries = sortEntry.sort_summary?.filter(s => !s.bill_qty && s.aggregated_qty > 0);
    if (unsetEntries?.length > 0) {
      toast.error(`Please set Bill Qty for all sort numbers. Missing: ${unsetEntries.map(s => s.sort_no).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await fobApi.confirmSummary(selectedConsignment.entry_id);
      toast.success(result.message);
      setSortEntry(result.sort_entry);
      setCurrentStep('print');
    } catch (error) {
      console.error('Error confirming summary:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm summary');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================
  // STEP 4: Print USNs per Bundle
  // ========================
  const handleViewBundleUsns = async (bundleBarcode) => {
    try {
      const result = await fobApi.getBundleUsns(selectedConsignment.entry_id, bundleBarcode);
      setSelectedBundleUsns({
        bundle: bundleBarcode,
        sortEntries: result.sort_entries || [],
        totalUsns: result.total_usns
      });
      setShowUsnDialog(true);
    } catch (error) {
      console.error('Error fetching bundle USNs:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch USNs');
    }
  };

  const handlePrintBundle = async (bundleBarcode) => {
    setPrintingBundle(bundleBarcode);
    try {
      const result = await fobApi.getBundleUsns(selectedConsignment.entry_id, bundleBarcode);
      // Collect all USN codes from the bundle with their Sort Nos
      const allUsns = [];
      const allSortNos = [];
      for (const sortEntry of result.sort_entries || []) {
        const sortNo = sortEntry.sort_no;
        const usns = sortEntry.usns || [];
        allUsns.push(...usns);
        // Add Sort No for each USN
        allSortNos.push(...usns.map(() => `Sort: ${sortNo}`));
      }
      
      if (allUsns.length === 0) {
        toast.error('No USNs found for this bundle');
        return;
      }
      
      setPrintCodes(allUsns);
      setPrintSortNos(allSortNos);
      setShowPrintDialog(true);
    } catch (error) {
      console.error('Error fetching bundle USNs for print:', error);
      toast.error(error.response?.data?.detail || 'Failed to load USNs for printing');
    }
  };

  const handleMarkBundlePrinted = async (bundleBarcode) => {
    try {
      const result = await fobApi.markBundlePrinted(selectedConsignment.entry_id, bundleBarcode);
      toast.success('Marked as printed');
      
      // Refresh
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      
      if (result.all_printed) {
        toast.success('All bundles printed! USN Tagging complete.');
      }
    } catch (error) {
      console.error('Error marking printed:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as printed');
    }
  };

  // ========================
  // Edit Document Barcode
  // ========================
  const handleOpenEditBarcodeDialog = () => {
    setNewBarcode(selectedConsignment?.document_barcode || '');
    setShowEditBarcodeDialog(true);
  };

  const handleUpdateBarcode = async () => {
    if (!newBarcode.trim()) {
      toast.error('Please enter a new barcode');
      return;
    }

    if (newBarcode.trim() === selectedConsignment?.document_barcode) {
      setShowEditBarcodeDialog(false);
      return;
    }

    setIsSubmitting(true);
    try {
      await fobApi.updateDocumentBarcode(selectedConsignment.entry_id, newBarcode.trim());
      toast.success('Document barcode updated successfully');
      setShowEditBarcodeDialog(false);
      
      // Refresh consignments and sort entry
      await fetchConsignments();
      const entry = await fobApi.getSortEntry(selectedConsignment.entry_id);
      setSortEntry(entry);
      
      // Update selected consignment
      setSelectedConsignment(prev => ({
        ...prev,
        document_barcode: newBarcode.trim()
      }));
    } catch (error) {
      console.error('Error updating barcode:', error);
      toast.error(error.response?.data?.detail || 'Failed to update barcode');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================
  // NEW: View USNs by Sort No
  // ========================
  const handleViewSortUsns = async (sortNo) => {
    try {
      const result = await fobApi.getSortUsns(selectedConsignment.entry_id, sortNo);
      setSelectedSortUsns({
        sort_no: sortNo,
        fg_barcode: result.fg_barcode || '',
        total_qty: result.total_qty || 0,
        bundles: result.bundles || [],
        total_usns: result.total_usns || 0
      });
      setShowSortUsnDialog(true);
    } catch (error) {
      console.error('Error fetching sort USNs:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch USNs');
    }
  };

  // ========================
  // Helpers
  // ========================
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBundlesProcessedCount = () => sortEntry?.bundles?.length || 0;
  const getBundlesTotalCount = () => sortEntry?.expected_bundles?.length || 0;
  const allBundlesProcessed = () => getBundlesProcessedCount() === getBundlesTotalCount() && getBundlesTotalCount() > 0;

  const isUsnAssigned = sortEntry?.status === 'usn_assigned' || sortEntry?.status === 'completed';
  const isCompleted = sortEntry?.status === 'completed';
  const isPrepasted = sortEntry?.sticker_mode === 'prepasted';

  // Step indicators
  const getStepStatus = (step) => {
    if (!sortEntry) return 'pending';
    
    const status = sortEntry.status;
    
    if (step === 'document') {
      return selectedConsignment ? 'completed' : 'active';
    }
    if (step === 'bundles') {
      if (status === 'in_progress') return currentStep === 'bundles' ? 'active' : 'pending';
      return 'completed';
    }
    if (step === 'summary') {
      if (status === 'bundles_complete') return 'active';
      if (status === 'usn_assigned' || status === 'completed') return 'completed';
      return 'pending';
    }
    if (step === 'print') {
      if (status === 'usn_assigned') return 'active';
      if (status === 'completed') return 'completed';
      return 'pending';
    }
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Tag className="w-6 h-6" />
              <div>
                <h1 className="text-xl font-bold font-display">Sort No → USN Tagging</h1>
                <p className="text-xs text-[#E8DCC8]">FOB Section - Operation 2 (Bundle-Centric)</p>
              </div>
            </div>
          </div>
          <Button onClick={fetchConsignments} variant="secondary" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Step Progress Indicator - Simplified for per-bundle workflow */}
      <div className="bg-white border-b border-[#E5DFD0] py-3 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Progress Tracker - Assigned Qty / Document Qty */}
          {selectedConsignment && (!isPrepasted || poLine) && (
            <div className="flex flex-wrap items-center justify-center gap-6 mb-3">
              {!isPrepasted && (
              <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-center">
                  <p className="text-xs text-blue-600 font-medium">Assignment Progress</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {assignmentProgress.assigned_qty} <span className="text-blue-400">/</span> {assignmentProgress.document_qty}
                  </p>
                </div>
                <div className="h-10 w-px bg-blue-200" />
                <div className="text-center">
                  <p className="text-xs text-blue-600 font-medium">Completed</p>
                  <p className="text-2xl font-bold text-blue-700">{assignmentProgress.progress_percentage}%</p>
                </div>
              </div>
              )}
              {poLine && (
                <div className="px-4 py-2 bg-white rounded-lg border border-blue-200 w-72" data-testid="fob-po-line-progress">
                  <p className="text-xs text-blue-600 font-medium truncate">{poSel.po.po_number} · {poLine.supplier_design_name} — received vs ordered</p>
                  <LineProgress line={poLine} />
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-center gap-2 text-sm">
            {['document', 'bundles'].map((step, idx) => {
              const status = getStepStatus(step);
              const labels = {
                document: '1. Select Document',
                bundles: '2. Process Bundles (One at a time)'
              };
              
              return (
                <React.Fragment key={step}>
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
                    status === 'completed' ? 'bg-green-100 text-green-700' :
                    status === 'active' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {status === 'completed' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 flex items-center justify-center text-xs font-medium">{idx + 1}</span>
                    )}
                    <span className="font-medium hidden sm:inline">{labels[step].split('. ')[1]}</span>
                  </div>
                  {idx < 1 && <ChevronRight className="w-4 h-4 text-gray-400" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Document Selection & Bundle Entry */}
          <div className="lg:col-span-1 space-y-4">
            {/* Document Selection */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
              <Label className="text-sm font-semibold text-[#1A1F2E]">Step 1: Select Document</Label>
              <Select
                value={selectedConsignment?.entry_id || ''}
                onValueChange={handleSelectConsignment}
                disabled={isLoading || isUsnAssigned}
              >
                <SelectTrigger className="mt-2" data-testid="document-select">
                  <SelectValue placeholder={isLoading ? "Loading..." : "Select document"} />
                </SelectTrigger>
                <SelectContent>
                  {consignments.map((consignment) => (
                    <SelectItem key={consignment.entry_id} value={consignment.entry_id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{consignment.document_barcode}</span>
                        <span className="text-xs text-gray-500">
                          Bundles: {consignment.bundles_processed}/{consignment.bundles_total} | Qty: {consignment.document_qty || 'N/A'}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedConsignment && (
                <div className="mt-3 p-3 bg-[#F5F1E8] rounded-lg text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <div><span className="text-[#5A5F6E]">Document:</span> <strong className="font-mono">{selectedConsignment.document_barcode}</strong></div>
                    <Button
                      onClick={handleOpenEditBarcodeDialog}
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      data-testid="edit-barcode-btn"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                  <div><span className="text-[#5A5F6E]">Vendor:</span> <strong>{selectedConsignment.vendor_name || 'N/A'}</strong></div>
                  <div><span className="text-[#5A5F6E]">Vendor Doc No:</span> <strong>{selectedConsignment.vendor_doc_no || 'N/A'}</strong></div>
                  <div><span className="text-[#5A5F6E]">Document Qty:</span> <strong className="text-blue-600">{sortEntry?.document_qty || 0}</strong></div>
                  <div><span className="text-[#5A5F6E]">Bundles:</span> <strong>{getBundlesTotalCount()}</strong></div>
                </div>
              )}
            </div>

            {/* Purchase Order — pick the sort no, then the pending PO row its tagged qty counts on */}
            {selectedConsignment && sortEntry && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 space-y-2" data-testid="fob-po-block">
                <Label className="text-sm font-semibold text-[#1A1F2E]">Sort No &amp; pending Purchase Orders</Label>
                <p className="text-xs text-[#5A5F6E]">Enter the sort no — its pending PO rows show with the pending qty and rate. A bundle past the pending qty asks for the next PO.</p>
                <SearchSelect value={poSortNo} onChange={pickPoSort} placeholder="Select sort no" searchPlaceholder="Search sort no or design…"
                  empty="No sort no for this vendor" testId="fob-po-sort-select"
                  options={sortNos.map((s) => ({ value: s.sort_no, label: s.sort_no, sub: [s.design_name, s.fg_name && `FG ${s.fg_name}`, s.description].filter(Boolean).join(' · ') }))} />
                <SortPoRows section="FOB" vendorName={selectedConsignment.vendor_name} sortNo={poSortNo} lineId={poSel.line?.po_item_id} none={poSel.none}
                  onChange={handlePoChange} refreshKey={poKey} testPrefix="fob-po" />
              </div>
            )}

            {/* Bundle Scanning - Step 2 */}
            {sortEntry && currentStep === 'bundles' && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <Label className="text-sm font-semibold text-[#1A1F2E]">Step 2: Scan Bundle</Label>
                
                {!currentBundle ? (
                  <form onSubmit={handleScanBundle} className="mt-3">
                    <div className="flex gap-2">
                      <Input
                        ref={bundleInputRef}
                        value={bundleBarcode}
                        onChange={(e) => setBundleBarcode(e.target.value)}
                        placeholder="Scan bundle barcode"
                        className="flex-1"
                        data-testid="bundle-barcode-input"
                        autoFocus
                      />
                      <Button type="submit" disabled={isSubmitting} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="fob-sort-scan-btn">
                        <Scan className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-[#5A5F6E] mt-2">
                      Processed: {getBundlesProcessedCount()} / {getBundlesTotalCount()} bundles
                    </p>
                  </form>
                ) : (
                  <div className="mt-3 space-y-3">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-700">{currentBundle.barcode}</span>
                      </div>
                      {currentBundle.existingEntries?.length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          {currentBundle.existingEntries.length} sort entries added
                        </p>
                      )}
                    </div>

                    {/* Add Sort Entry Form */}
                    <form onSubmit={handleAddBundleSort} className="space-y-3">
                      {/* Sort No Selection with Search - Vendor comes from document */}
                      <div>
                        <Label className="text-xs text-[#5A5F6E]">Sort No *{lineSortLocked && <span className="ml-1 text-purple-600">from the PO line</span>}</Label>
                        <Popover open={sortNoPopoverOpen} onOpenChange={setSortNoPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full h-10 mt-1 justify-between text-left font-normal"
                              disabled={lineSortLocked}
                              data-testid="sort-no-select"
                            >
                              <span className={selectedSortNo ? "text-gray-900" : "text-gray-500"}>
                                {selectedSortNo ? (
                                  <>
                                    {selectedSortNo}
                                    {getSelectedSortNoData()?.design_name && (
                                      <span className="text-purple-600 ml-2">- {getSelectedSortNoData().design_name}</span>
                                    )}
                                  </>
                                ) : (
                                  "Select sort no..."
                                )}
                              </span>
                              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0" align="start">
                            <Command>
                              <CommandInput 
                                placeholder="Search sort no or design..." 
                                value={sortNoSearch}
                                onValueChange={setSortNoSearch}
                              />
                              <CommandList className="max-h-[200px]">
                                <CommandEmpty>No sort no found for this vendor.</CommandEmpty>
                                <CommandGroup>
                                  {filteredSortNos.map((sort) => (
                                    <CommandItem
                                      key={sort.sort_no}
                                      value={`${sort.sort_no} ${sort.design_name || ''}`}
                                      onSelect={() => handleSortNoDropdownSelect(sort)}
                                      className="cursor-pointer"
                                    >
                                      <div>
                                        <p className="font-medium">{sort.sort_no}</p>
                                        {sort.design_name ? (
                                          <p className="text-xs text-purple-600 font-medium">{sort.design_name}</p>
                                        ) : sort.description ? (
                                          <p className="text-xs text-gray-500">{sort.description}</p>
                                        ) : null}
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        
                        {/* FG Barcode Display */}
                        {fgBarcode && (
                          <div className={`mt-2 p-2 rounded text-xs ${
                            fgBarcode.barcode ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                          }`}>
                            {fgBarcode.barcode ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-green-700 font-semibold">FG Barcode:</span>
                                  <span className="font-mono text-green-800">{fgBarcode.barcode}</span>
                                </div>
                                {fgBarcode.designName && (
                                  <div className="text-purple-600 font-medium">Design: {fgBarcode.designName}</div>
                                )}
                                {fgBarcode.size && (
                                  <div className="text-green-600">Size: {fgBarcode.size}</div>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-yellow-700">
                                <AlertTriangle className="w-3 h-3" />
                                <span>FG Barcode not available</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-[#5A5F6E]">Actual Qty *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={actualQty}
                          onChange={(e) => setActualQty(e.target.value)}
                          placeholder="0"
                          className="mt-1"
                          data-testid="actual-qty-input"
                        />
                      </div>
                      {poLine && !isPcs(poLine.unit) && (
                        <div>
                          <Label className="text-xs text-[#5A5F6E]">Qty for the PO line ({poLine.unit}) *</Label>
                          <Input type="number" min="0" step="0.01" value={poQty} onChange={(e) => setPoQty(e.target.value)}
                            placeholder="0" className="mt-1" data-testid="po-qty-input" />
                        </div>
                      )}
                      <Button 
                        type="submit" 
                        disabled={isSubmitting || !sortNo.trim() || !actualQty}
                        className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]"
                        data-testid="add-sort-btn"
                      >
                        {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Add Sort Entry
                      </Button>
                    </form>

                    {/* Current Bundle Sort Entries */}
                    {currentBundle.existingEntries?.length > 0 && (
                      <div className="border-t border-[#E5DFD0] pt-3">
                        <Label className="text-xs text-[#5A5F6E]">Entries for this bundle:</Label>
                        <div className="mt-2 space-y-1">
                          {currentBundle.existingEntries.map((entry) => (
                            <div key={entry.sort_no} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                              <span><strong>{entry.sort_no}</strong> - Qty: {entry.actual_qty}
                                {entry.po_number && <span className="ml-2 text-[11px] font-mono text-blue-700" data-testid={`fob-entry-po-${entry.sort_no}`}>{entry.po_number}</span>}</span>
                              {!currentBundle.usnAssigned && (
                                <Button
                                  onClick={() => handleRemoveBundleSort(currentBundle.barcode, entry.sort_no)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Per-Bundle Action Buttons */}
                    <div className="border-t border-[#E5DFD0] pt-3 space-y-2">
                      {isPrepasted && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800" data-testid="prepasted-note">
                          <p className="font-semibold mb-1">Pre-pasted USN mode</p>
                          <p className="text-xs">Enter every sort no + qty for this bundle here. Then scan the pre-printed stickers in <strong>Assortment Packing</strong> to tag &amp; pack them — no USN assign/print needed.</p>
                          <Button onClick={handleNextBundle} variant="outline" className="w-full mt-2" data-testid="next-bundle-btn-prepasted">
                            Done → Next Bundle
                          </Button>
                        </div>
                      )}
                      {/* Step 1: Assign USN (if not already assigned) */}
                      {!isPrepasted && !currentBundle.usnAssigned && currentBundle.existingEntries?.length > 0 && (
                        <Button
                          onClick={handleAssignBundleUsn}
                          disabled={isSubmitting}
                          className="w-full bg-purple-600 hover:bg-purple-700"
                          data-testid="assign-bundle-usn-btn"
                        >
                          {isSubmitting ? (
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Tag className="w-4 h-4 mr-2" />
                          )}
                          Assign USN to this Bundle
                        </Button>
                      )}
                      
                      {/* Step 2: Print Stickers (after USN assigned) */}
                      {!isPrepasted && currentBundle.usnAssigned && (
                        <>
                          <Button
                            onClick={handlePrintCurrentBundle}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            data-testid="print-bundle-btn"
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            Print USN Stickers
                          </Button>

                          {/* FG Sticker (main label) print - same output as SFG Main Label */}
                          <Button
                            onClick={handlePrintCurrentBundleFGSticker}
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            data-testid="print-fg-sticker-btn"
                          >
                            <Tag className="w-4 h-4 mr-2" />
                            Print FG Sticker
                          </Button>
                          
                          {/* Step 3: Mark as Completed */}
                          <Button
                            onClick={handleMarkCurrentBundlePrinted}
                            className="w-full bg-green-600 hover:bg-green-700"
                            data-testid="complete-bundle-btn"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Mark as Completed → Next Bundle
                          </Button>
                        </>
                      )}
                      
                      {/* Skip to next bundle without completing */}
                      {!isPrepasted && !currentBundle.usnAssigned && (
                        <Button
                          onClick={handleNextBundle}
                          variant="outline"
                          className="w-full"
                          data-testid="next-bundle-btn"
                        >
                          Skip → Next Bundle
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Processed Bundles Count */}
                {getBundlesProcessedCount() > 0 && !currentBundle && (
                  <div className="mt-4 pt-4 border-t border-[#E5DFD0]">
                    <p className="text-sm text-center text-[#5A5F6E]">
                      Processed: {getBundlesProcessedCount()} / {getBundlesTotalCount()} bundles
                    </p>
                    {!allBundlesProcessed() && (
                      <p className="text-xs text-orange-600 mt-1 text-center">
                        {getBundlesTotalCount() - getBundlesProcessedCount()} bundles remaining
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Summary Stats */}
            {sortEntry && (currentStep === 'summary' || currentStep === 'print') && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <Label className="text-sm font-semibold text-[#1A1F2E]">Summary Stats</Label>
                <div className="mt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Document Qty:</span>
                    <span className="font-semibold">{sortEntry.document_qty}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Total Scanned Qty:</span>
                    <span className="font-semibold">{sortEntry.total_scanned_qty}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Sort Numbers:</span>
                    <span className="font-semibold">{sortEntry.sort_summary?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#5A5F6E]">Bundles:</span>
                    <span className="font-semibold">{sortEntry.bundles?.length || 0}</span>
                  </div>
                </div>
                
                {currentStep === 'summary' && (
                  <Button
                    onClick={handleConfirmSummary}
                    disabled={isSubmitting}
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    data-testid="confirm-summary-btn"
                  >
                    {isSubmitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Confirm Summary & Assign FG Barcodes + USNs
                  </Button>
                )}

                {isUsnAssigned && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto" />
                    <p className="text-sm text-green-700 font-medium mt-1">USNs Assigned</p>
                    <p className="text-xs text-green-600 mt-1">Print USNs per bundle below</p>
                  </div>
                )}
              </div>
            )}

            {/* No Selection State */}
            {!selectedConsignment && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-[#5A5F6E]">Select a document to begin</p>
              </div>
            )}
          </div>

          {/* Right Panel - Data Display */}
          <div className="lg:col-span-2 space-y-4">
            {/* Bundles List (Step 2) */}
            {sortEntry && currentStep === 'bundles' && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-sm font-semibold text-[#1A1F2E]">
                    Bundles Processed ({getBundlesProcessedCount()} / {getBundlesTotalCount()})
                  </Label>
                </div>
                
                {sortEntry.bundles?.length > 0 ? (
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                          <TableHead className="font-semibold text-[#1A1F2E]">Bundle Barcode</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Sort Entries</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Total Qty</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Status</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Actions</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Print Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortEntry.bundles.map((bundle) => {
                          const totalQty = bundle.sort_entries?.reduce((sum, s) => sum + s.actual_qty, 0) || 0;
                          const isUsnAssigned = bundle.usn_assigned;
                          const isPrinted = bundle.usns_printed;
                          const isCompleted = isUsnAssigned && isPrinted;
                          
                          return (
                            <TableRow 
                              key={bundle.bundle_barcode} 
                              className={`hover:bg-[#FFFBF5] ${isCompleted ? 'bg-green-50' : ''}`}
                            >
                              <TableCell className="font-mono text-sm">
                                {isCompleted && <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />}
                                {bundle.bundle_barcode}
                              </TableCell>
                              <TableCell className="text-center">
                                {bundle.sort_entries?.map(s => s.sort_no).join(', ')}
                              </TableCell>
                              <TableCell className="text-center font-semibold">{totalQty}</TableCell>
                              <TableCell className="text-center">
                                {isCompleted ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                                    ✓ Completed
                                  </span>
                                ) : isUsnAssigned ? (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                    USN Assigned
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                    Pending
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {!isCompleted ? (
                                  <Button
                                    onClick={() => {
                                      setActualQty('');
                                      setPoQty('');
                                      setCurrentBundle({
                                        barcode: bundle.bundle_barcode,
                                        alreadyProcessed: true,
                                        existingEntries: bundle.sort_entries || [],
                                        usnAssigned: bundle.usn_assigned || false
                                      });
                                    }}
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600"
                                  >
                                    <Edit2 className="w-4 h-4 mr-1" /> {isUsnAssigned ? 'Print' : 'Edit'}
                                  </Button>
                                ) : (
                                  <span className="text-xs text-green-600">Done</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {bundle.last_printed_at ? (
                                  <div className="flex flex-col items-center gap-0.5" data-testid={`print-status-${bundle.bundle_barcode}`}>
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium inline-flex items-center gap-1">
                                      <Printer className="w-3 h-3" /> Printed{bundle.print_count > 1 ? ` ×${bundle.print_count}` : ''}
                                    </span>
                                    <span className="text-[10px] text-gray-500">{formatDateTime(bundle.last_printed_at)}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400" data-testid={`print-status-${bundle.bundle_barcode}`}>Not printed</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#5A5F6E]">
                    <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No bundles processed yet</p>
                    <p className="text-sm">Scan a bundle barcode to start</p>
                  </div>
                )}

                <SortConsolidatedTable bundles={sortEntry.bundles || []} documentQty={sortEntry.document_qty} />

                {/* Pending Bundles List */}
                {sortEntry.expected_bundles?.some(b => !sortEntry.bundles?.find(pb => pb.bundle_barcode === b)) && (
                  <div className="mt-4 pt-4 border-t border-[#E5DFD0]">
                    <Label className="text-xs text-[#5A5F6E]">Pending Bundles:</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {sortEntry.expected_bundles
                        .filter(b => !sortEntry.bundles?.find(pb => pb.bundle_barcode === b))
                        .map(bundle => (
                          <span key={bundle} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded font-mono">
                            {bundle}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Summary Table (Step 3) */}
            {sortEntry && currentStep === 'summary' && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-sm font-semibold text-[#1A1F2E]">
                    Step 3: Summary Report - Enter Bill Qty
                  </Label>
                </div>
                
                {sortEntry.sort_summary?.length > 0 ? (
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                          <TableHead className="font-semibold text-[#1A1F2E]">Sort No</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">FG Barcode</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Aggregated Qty</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Bill Qty</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Shortage</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Excess</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Remarks</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortEntry.sort_summary.map((summary) => (
                          <TableRow key={summary.sort_no} className="hover:bg-[#FFFBF5]">
                            <TableCell className="font-medium">{summary.sort_no}</TableCell>
                            <TableCell className="font-mono text-xs text-purple-700">
                              {summary.fg_barcode || <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-center font-semibold">{summary.aggregated_qty}</TableCell>
                            <TableCell className="text-center">
                              {editingSortNo === summary.sort_no ? (
                                <Input
                                  type="number"
                                  min="0"
                                  value={billQtyInput}
                                  onChange={(e) => setBillQtyInput(e.target.value)}
                                  className="w-20 h-8 text-center"
                                  autoFocus
                                />
                              ) : (
                                <span className={summary.bill_qty ? 'text-blue-600 font-semibold' : 'text-gray-400'}>
                                  {summary.bill_qty || '-'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center text-orange-600">
                              {summary.shortage > 0 ? summary.shortage : '-'}
                            </TableCell>
                            <TableCell className="text-center text-green-600">
                              {summary.excess > 0 ? summary.excess : '-'}
                            </TableCell>
                            <TableCell className="text-sm text-[#5A5F6E] max-w-[150px]">
                              {editingSortNo === summary.sort_no ? (
                                <Input
                                  value={remarksInput}
                                  onChange={(e) => setRemarksInput(e.target.value)}
                                  placeholder="Remarks"
                                  className="h-8 text-sm"
                                />
                              ) : (
                                <span className="truncate block" title={summary.remarks || ''}>
                                  {summary.remarks || '-'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {editingSortNo === summary.sort_no ? (
                                <div className="flex gap-1">
                                  <Button
                                    onClick={handleSaveBillQty}
                                    disabled={isSubmitting}
                                    size="sm"
                                    className="h-7 bg-green-600 hover:bg-green-700"
                                  >
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    onClick={() => setEditingSortNo(null)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => handleEditBillQty(summary)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-blue-600"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#5A5F6E]">
                    <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No summary data</p>
                  </div>
                )}

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Enter Bill Qty for each sort number. Shortage/Excess is auto-calculated.
                    Remarks is mandatory when there is a discrepancy.
                  </p>
                </div>
              </div>
            )}

            {/* Print USNs per Bundle (Step 4) */}
            {sortEntry && currentStep === 'print' && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-sm font-semibold text-[#1A1F2E]">
                    Step 4: Print USNs per Bundle
                  </Label>
                  {isCompleted && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      ✓ All Printed
                    </span>
                  )}
                </div>
                
                {sortEntry.bundles?.length > 0 ? (
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                          <TableHead className="font-semibold text-[#1A1F2E]">Bundle Barcode</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Sort Entries</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Total Qty</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Status</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortEntry.bundles.map((bundle) => {
                          const totalQty = bundle.sort_entries?.reduce((sum, s) => sum + s.actual_qty, 0) || 0;
                          const isPrinted = bundle.usns_printed;
                          
                          return (
                            <TableRow key={bundle.bundle_barcode} className="hover:bg-[#FFFBF5]">
                              <TableCell className="font-mono text-sm">{bundle.bundle_barcode}</TableCell>
                              <TableCell className="text-center text-sm">
                                {bundle.sort_entries?.map(s => `${s.sort_no}(${s.actual_qty})`).join(', ')}
                              </TableCell>
                              <TableCell className="text-center font-semibold">{totalQty}</TableCell>
                              <TableCell className="text-center">
                                {isPrinted ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                    ✓ Printed
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                                    Pending
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    onClick={() => handleViewBundleUsns(bundle.bundle_barcode)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-purple-600"
                                  >
                                    View USNs
                                  </Button>
                                  {!isPrinted && (
                                    <Button
                                      onClick={() => handlePrintBundle(bundle.bundle_barcode)}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-blue-600"
                                      data-testid={`print-bundle-${bundle.bundle_barcode}`}
                                    >
                                      <Printer className="w-4 h-4 mr-1" />
                                      Print
                                    </Button>
                                  )}
                                  {!isPrinted && (
                                    <Button
                                      onClick={() => handleMarkBundlePrinted(bundle.bundle_barcode)}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 text-green-600"
                                    >
                                      <Check className="w-4 h-4 mr-1" />
                                      Done
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#5A5F6E]">
                    <Printer className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No bundles to print</p>
                  </div>
                )}

                {/* Summary Table - View Only */}
                <div className="mt-6 pt-4 border-t border-[#E5DFD0]">
                  <Label className="text-sm font-semibold text-[#1A1F2E] mb-3 block">Summary (USNs Assigned)</Label>
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                          <TableHead className="font-semibold text-[#1A1F2E]">Sort No</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E]">FG Barcode</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Aggregated</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Bill Qty</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">Shortage/Excess</TableHead>
                          <TableHead className="font-semibold text-[#1A1F2E] text-center">USNs Assigned</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortEntry.sort_summary?.map((summary) => (
                          <TableRow key={summary.sort_no} className="hover:bg-[#FFFBF5]">
                            <TableCell className="font-medium">{summary.sort_no}</TableCell>
                            <TableCell className="font-mono text-xs text-purple-700">
                              {summary.fg_barcode || <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-center">{summary.aggregated_qty}</TableCell>
                            <TableCell className="text-center">{summary.bill_qty}</TableCell>
                            <TableCell className="text-center">
                              {summary.shortage > 0 && <span className="text-orange-600">-{summary.shortage}</span>}
                              {summary.excess > 0 && <span className="text-green-600">+{summary.excess}</span>}
                              {!summary.shortage && !summary.excess && <span className="text-gray-400">-</span>}
                            </TableCell>
                            <TableCell className="text-center font-semibold text-purple-600">
                              <button
                                onClick={() => handleViewSortUsns(summary.sort_no)}
                                className="underline hover:text-purple-800 cursor-pointer"
                                title="Click to view USN details"
                              >
                                {summary.assigned_usns?.length || 0}
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}

            {/* No Selection State */}
            {!selectedConsignment && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-8 text-center">
                <Tag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">Select a Document</h3>
                <p className="text-[#5A5F6E]">Choose a document from the dropdown to start USN tagging</p>
                <p className="text-sm text-[#5A5F6E] mt-2">Documents appear here as soon as at least one bundle is received &mdash; no need to wait for the whole receipt</p>
              </div>
            )}
          </div>
        </div>
        <NextPoDialog ask={ask} onPick={onNextPo} onCancel={() => setAsk(null)} />
      </main>

      {/* USN List Dialog for Bundle */}
      <Dialog open={showUsnDialog} onOpenChange={setShowUsnDialog}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>USNs for Bundle: {selectedBundleUsns.bundle}</DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {selectedBundleUsns.sortEntries?.length > 0 ? (
              <div className="space-y-4">
                {selectedBundleUsns.sortEntries.map((entry) => (
                  <div key={entry.sort_no} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold">Sort No: {entry.sort_no}</span>
                      <span className="text-sm text-gray-500">Qty: {entry.qty}</span>
                    </div>
                    <div className="space-y-1">
                      {entry.usns?.map((usn, idx) => (
                        <div 
                          key={usn} 
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm font-mono"
                        >
                          <span className="text-gray-400 w-6">{idx + 1}.</span>
                          <span>{usn}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500">No USNs assigned to this bundle</p>
            )}
            <div className="mt-4 pt-3 border-t border-gray-200 text-center">
              <span className="text-sm text-gray-600">Total USNs: <strong>{selectedBundleUsns.totalUsns}</strong></span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowUsnDialog(false)} variant="secondary">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW: USN List Dialog for Sort No (from Summary Table) */}
      <Dialog open={showSortUsnDialog} onOpenChange={setShowSortUsnDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-500" />
              USNs for Sort No: {selectedSortUsns.sort_no}
            </DialogTitle>
            {selectedSortUsns.fg_barcode && (
              <p className="text-sm text-gray-600 mt-1">
                FG Barcode: <span className="font-mono font-semibold text-purple-600">{selectedSortUsns.fg_barcode}</span>
              </p>
            )}
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-y-auto">
            {selectedSortUsns.bundles?.length > 0 ? (
              <div className="space-y-4">
                {selectedSortUsns.bundles.map((bundleEntry) => (
                  <div key={bundleEntry.bundle_barcode} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2 bg-blue-50 p-2 rounded">
                      <span className="font-semibold text-blue-700">
                        <Package className="w-4 h-4 inline mr-1" />
                        Bundle: {bundleEntry.bundle_barcode}
                      </span>
                      <span className="text-sm text-blue-600">Qty: {bundleEntry.qty}</span>
                    </div>
                    <div className="space-y-1">
                      {bundleEntry.usns?.map((usn, idx) => (
                        <div 
                          key={usn} 
                          className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm font-mono"
                        >
                          <span className="text-gray-400 w-8">{idx + 1}.</span>
                          <span>{usn}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500">No USNs assigned to this sort number</p>
            )}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <span>Total Quantity: <strong className="text-blue-600">{selectedSortUsns.total_qty}</strong></span>
                <span>Total USNs: <strong className="text-purple-600">{selectedSortUsns.total_usns}</strong></span>
                <span>Bundles: <strong>{selectedSortUsns.bundles?.length || 0}</strong></span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSortUsnDialog(false)} variant="secondary">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <PrintDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        codes={printCodes}
        sortNos={printSortNos}
        labelType="usn"
        defaultSection="FOB"
        operationKey="fob-sortno-tagging"
        title="Print USN Labels"
        onPrinted={handleRecordPrint}
      />

      <FGStickerPrintDialog
        open={showFgStickerDialog}
        onOpenChange={setShowFgStickerDialog}
        items={fgStickerItems}
        title="Print FG Stickers"
        onPrinted={handleRecordPrint}
      />

      {/* Edit Barcode Dialog */}
      <Dialog open={showEditBarcodeDialog} onOpenChange={setShowEditBarcodeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-500" />
              Update Document Barcode
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>Warning:</strong> Updating the barcode will change it across all related records including USNs, assortment entries, and packing records.
              </p>
            </div>
            <div>
              <Label className="text-sm text-[#5A5F6E]">Current Barcode</Label>
              <p className="font-mono font-semibold text-lg text-[#1A1F2E] mt-1">
                {selectedConsignment?.document_barcode}
              </p>
            </div>
            <div>
              <Label className="text-sm text-[#5A5F6E]">New Barcode</Label>
              <Input
                value={newBarcode}
                onChange={(e) => setNewBarcode(e.target.value)}
                placeholder="Enter new barcode"
                className="mt-1 font-mono"
                data-testid="new-barcode-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditBarcodeDialog(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateBarcode}
              disabled={isSubmitting || !newBarcode.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-barcode-btn"
            >
              {isSubmitting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Update Barcode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SortNoUsnTagging;
